/**
 * Store pour le chargement des tâches en brouillon pour le client connecté
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

export const useAuthWritingTaskDraftStore = defineStore('authWritingTaskDraft', (() => {

    // State de la liste des tâches en brouillon chargées (total, tasks)
    const draftTaskList = ref({});

    // State pour différencier les données de la liste de tout les brouillons chargés
    const draftTasks = ref([]);

    /**
     * Retourne la liste des tâches en brouillon
     * @return array
     */
    const getDraftTaskList = computed(() => draftTaskList.value || {});

    /**
     * Chargement de la liste des brouillons
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadDraftTaskList = ((params = {}) => {

        const path = '/api/me/tasks/draft';

        if(! params.limit) {
            params.limit = 20;
        }

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetDraftTaskList();
                }

                updateDraftTaskList(response.data.data);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Modifie la liste des tâches en brouillon dans le store
     * @param data Données des offres : total et items
     * @return void
     */
    const updateDraftTaskList = ((data) => {

        const currentData = draftTaskList.value;
        if(currentData.total === undefined) {
            draftTaskList.value = {
                total: data.total,
                tasks: data.items
            };

            data.items.forEach((task) => {
                updateDraftTask(task);
            });

        } else {
            draftTaskList.value.total = data.total;

            const currentTasks = draftTaskList.value.tasks;
            const newTasks = data.items.filter((item) => ! currentTasks.find((current) => current.publicId === item.publicId));
            draftTaskList.value.tasks = [ ...currentTasks, ...newTasks ];

            newTasks.forEach((task) => {
                updateDraftTask(task);
            });
        }

    });

    /**
     * Modifie un brouillon dans le store
     * @param object task Données de la tâche à mettre à jour dans le store
     * @return Promise
     */
    const updateDraftTask = ((task) => {  // draftTasks

        const currentTasks = draftTasks.value;

        const index = currentTasks.findIndex((taskFromState) => taskFromState.publiId === task.publicId);
        if(index !== -1) {
            draftTasks.value[index] = { ...draftTasks.value[index], ...task };
        } else {
            draftTasks.value.push(task);
        }

    });

    /**
     * Méthode appliquée lors de la suppression d'une tâche
     * @param object task La tâche supprimée
     * @return void
     */
    const onRemoveTask = ((task) => {

        // Mise à jour dans les tâches chargées
        const indexToDelete = draftTasks.value.findIndex((taskFromState) => taskFromState.publicId === task.publicId);
        if(indexToDelete !== -1) {
            draftTasks.value.splice(indexToDelete, 1);
        }

        // Mise à jour dans la liste
        const draftListData = draftTaskList.value.tasks || [];
        const indexFromList = draftListData.findIndex((taskFromState) => taskFromState.publicId === task.publicId);
        if(indexFromList !== -1) {
            draftTaskList.value.tasks.splice(indexFromList, 1);
            draftTaskList.value.total = Math.max(0, draftTaskList.value.total - 1);
        }

    });

    /**
     * Réinitialisation de la liste des tâches en brouillon
     * @return void
     */
    const resetDraftTaskList = (() => {
        draftTaskList.value = {};
    });

    return {
        getDraftTaskList,
        updateDraftTaskList,
        loadDraftTaskList,
        resetDraftTaskList,
        onRemoveTask
    };
}));
